import { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL;

// Lista de participantes + distritais, compartilhada pela tela de convidados
// e pelo leitor de QR. Atualiza sozinha a cada 3s — assim um check-in feito
// em outro aparelho aparece sem precisar dar F5.
const useParticipantes = ({ intervaloMs = 3000 } = {}) => {
  const { user } = useContext(AuthContext);
  const role = user?.role;
  const [participantes, setParticipantes] = useState([]);
  const [distritaisMap, setDistritaisMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  const carregar = useCallback(async () => {
    const url =
      role === "admin" ? `${API}/participantes/listar-todos` : `${API}/participantes/listar`;

    const [participantesRes, distritaisRes] = await Promise.all([
      axios.get(url),
      axios.get(`${API}/distrital/listar`),
    ]);

    const mapa = {};
    distritaisRes.data.forEach((d) => {
      mapa[d.id] = d.nome;
    });

    return { lista: participantesRes.data, mapa };
  }, [role]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelado = false;

    const atualizar = async (primeiraVez) => {
      try {
        if (primeiraVez) setLoading(true);
        const { lista, mapa } = await carregar();
        if (cancelado) return;
        setParticipantes(lista);
        setDistritaisMap(mapa);
        setErro("");
        setAtualizadoEm(new Date());
      } catch (error) {
        if (!cancelado) {
          console.error(error);
          setErro("Erro ao carregar participantes ou distritais");
        }
      } finally {
        if (primeiraVez && !cancelado) setLoading(false);
      }
    };

    atualizar(true);
    const intervalo = setInterval(() => atualizar(false), intervaloMs);

    return () => {
      cancelado = true;
      clearInterval(intervalo);
    };
  }, [user, carregar, intervaloMs]);

  const registrarEntrada = useCallback(async (id) => {
    await axios.patch(`${API}/participantes/confirmar-presenca-id/${id}`, {});
    setParticipantes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, presenca: 1 } : p))
    );
  }, []);

  const salvarMesa = useCallback(async (id, mesa) => {
    await axios.patch(`${API}/participantes/mesa/${id}`, { mesa });
    setParticipantes((prev) => prev.map((p) => (p.id === id ? { ...p, mesa } : p)));
  }, []);

  const excluir = useCallback(async (id) => {
    await axios.patch(`${API}/participantes/excluir/${id}`, {});
    setParticipantes((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return {
    role,
    participantes,
    distritaisMap,
    loading,
    erro,
    atualizadoEm,
    registrarEntrada,
    salvarMesa,
    excluir,
  };
};

export default useParticipantes;
